import React, { useState, useEffect } from "react"
import MetaTags from "react-meta-tags"
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input
} from "reactstrap"
import classnames from "classnames"
import Breadcrumbs from "components/Common/Breadcrumb"
import Datatables from "components/Datatables/datatable"
import Select from "react-select"
import { get, post, baseURL } from "helpers/api_helper"
import { PAYROLL_URL } from "helpers/url_helper"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

// Import Settings View and its sub-components
import { PayrollSettingsView } from "./config"
import SalaryComponent from "./settings/SalaryComponent"
import SalaryGroup from "./settings/SalaryGroup"
import SSF from "./settings/SSF"
import Bonus from "./settings/Bonus"
import SalaryTDS from "./settings/SalaryTDS"
import AdvanceSalary from "./settings/AdvanceSalary"
import OverTime from "./settings/OverTime"
import UnderTime from "./settings/UnderTime"
import PaymentMethod from "./settings/PaymentMethod"

const Payroll = () => {
    const [activeTab, setActiveTab] = useState("1")
    const [activeSettingTab, setActiveSettingTab] = useState("1")
    const [isLoading, setIsLoading] = useState(false)
    const [payrollData, setPayrollData] = useState({ rows: [], total_rows: 0 })

    const [employeeList, setEmployeeList] = useState([])
    const [salaryGroups, setSalaryGroups] = useState([])
    const [salaryComponents, setSalaryComponents] = useState([])
    const [showSetupModal, setShowSetupModal] = useState(false)
    const [setupData, setSetupData] = useState({
        employee_id: "",
        employee_name: "",
        basic_salary: 0,
        salary_group: ""
    })

    const [pagination, setPagination] = useState({
        limit: 10,
        page: 1,
        search: ""
    })

    const [showGenerateModal, setShowGenerateModal] = useState(false)
    const [generateData, setGenerateData] = useState({
        branch_id: "",
        employee_id: "all",
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    })

    const [branchOptions, setBranchOptions] = useState([])
    const [employeeOptions, setEmployeeOptions] = useState([])

    const months = [
        { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
        { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
        { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
        { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
    ]

    const years = Array.from({ length: 5 }, (_, i) => ({ value: new Date().getFullYear() - i, label: (new Date().getFullYear() - i).toString() }))

    const toggleTab = tab => {
        if (activeTab !== tab) setActiveTab(tab)
    }

    const toggleSettingTab = tab => {
        if (activeSettingTab !== tab) setActiveSettingTab(tab)
    }

    const getData = async () => {
        setIsLoading(true)
        const res = await get(`${PAYROLL_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.search}`)
        if (res.status === 200) {
            setPayrollData(res.response)
        }
        setIsLoading(false)
    }

    const getEmployees = async () => {
        const res = await get("api/employee?limit=100")
        if (res.status === 200) {
            setEmployeeList(res.response.rows || [])
        }
    }

    const getSalaryGroups = async () => {
        const res = await get("api/hrms/payroll/groups")
        if (res.status === 200) {
            setSalaryGroups(res.response.rows || [])
        }
    }

    const getSalaryComponents = async () => {
        const res = await get("api/hrms/payroll/components")
        if (res.status === 200) {
            setSalaryComponents(res.response.rows || [])
        }
    }

    const fetchSelectors = async () => {
        const branchRes = await get("api/selectors/getBranchs")
        if (branchRes.status === 200) {
            setBranchOptions(branchRes.response.map(b => ({ value: b.id, label: b.title })))
        }

        const empRes = await post("api/selectors/getEmployees", { branch: "all" })
        if (empRes.status === 200) {
            setEmployeeOptions([{ value: "all", label: "All Employees" }, ...empRes.response])
        }
    }

    useEffect(() => {
        fetchSelectors()
        getSalaryGroups()
        getSalaryComponents()
    }, [])

    useEffect(() => {
        if (activeTab === "2") {
            getEmployees()
        }
    }, [activeTab])

    const handleGenerate = async () => {
        setIsLoading(true)
        const res = await post(PAYROLL_URL, generateData)
        if (res.status === 200 || res.status === true) {
            toastr.success("Payroll generated successfully")
            setShowGenerateModal(false)
            getData()
        } else {
            toastr.error(res.message || "Failed to generate payroll")
        }
        setIsLoading(false)
    }

    const handleSalarySetupUpdate = async () => {
        const res = await post(`${PAYROLL_URL}/salary_setup`, setupData)
        if (res.status === 200 || res.status === true) {
            toastr.success("Salary setup updated successfully")
            setShowSetupModal(false)
            getEmployees()
        } else {
            toastr.error("Failed to update salary setup")
        }
    }
    const handleStatusUpdate = async (id, data) => {
        const updateData = { ...data };
        if (data.status === "Paid") {
            updateData.is_locked = 1;
        }

        const res = await post(`${PAYROLL_URL}/status`, { id, ...updateData })
        if (res.status === 200 || res.status === true) {
            toastr.success("Status updated successfully")
            getData()
        } else {
            toastr.error("Failed to update status")
        }
    }

    useEffect(() => {
        if (activeTab === "1") {
            getData()
        }
    }, [pagination, activeTab])

    const brownBtnStyle = { backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }
    const greenBtnStyle = { backgroundColor: "#159858", borderColor: "#159858", color: "#fff" }
    const pinkActiveStyle = { backgroundColor: "#ff3366", color: "#fff", borderColor: "#ff3366" }

    const payrollColumns = [
        {
            dataField: "id",
            text: "#",
            formatter: (cell, row, index) => (pagination.page - 1) * pagination.limit + index + 1
        },
        {
            dataField: "employee_id",
            text: "EMPLOYEE",
            formatter: (cell, row) => (
                <div>
                    <div style={{ fontWeight: "bold", color: "#3d1e08" }}>{row.employee_name}</div>
                    <div style={{ fontSize: "11px", color: "#777" }}>ID: {row.employee_id}</div>
                </div>
            )
        },
        { dataField: "month_year", text: "MONTH/YEAR" },
        {
            dataField: "basic_salary",
            text: "BASIC",
            formatter: (cell) => `₹${cell}`
        },
        { 
            dataField: "allowance", 
            text: "ALLOWANCE",
            formatter: (cell, row) => (
                <div>
                    <span className="text-success" style={{ fontWeight: "bold" }}>+₹{cell || 0}</span>
                    <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>
                        {row.items?.filter(i => i.type === 'Earning').map((i, idx) => (
                            <div key={idx}>{i.name}: ₹{i.amount}</div>
                        ))}
                    </div>
                </div>
            )
        },
        { 
            dataField: "deduction", 
            text: "DEDUCTION",
            formatter: (cell, row) => (
                <div>
                    <span className="text-danger" style={{ fontWeight: "bold" }}>-₹{cell || 0}</span>
                    <div style={{ fontSize: "10px", color: "#666", marginTop: "2px" }}>
                        {row.items?.filter(i => i.type === 'Deduction').map((i, idx) => (
                            <div key={idx}>{i.name}: ₹{i.amount}</div>
                        ))}
                    </div>
                </div>
            )
        },
        {
            dataField: "net_salary",
            text: "NET PAY",
            formatter: (cell) => <strong className="text-success">₹{cell}</strong>
        },
        {
            dataField: "status",
            text: "STATUS",
            formatter: (cell, row) => (
                <span className={`badge ${cell === "Paid" ? "bg-success" : "bg-warning"}`} style={{ padding: "5px 10px" }}>
                    {cell} {row.is_locked == 1 && <i className="fas fa-lock ms-1"></i>}
                </span>
            )
        },
        {
            dataField: "action",
            text: "ACTIONS",
            formatter: (cell, row) => (
                <div className="d-flex">
                    <Button
                        color="light"
                        size="sm"
                        className="me-2"
                        title="Print Pay Slip"
                        onClick={() => window.open(`${baseURL}/api/hrms/payroll/print?id=${row.id}`, "_blank")}
                    >
                        <i className="fas fa-print text-primary"></i> Slip
                    </Button>

                    {row.is_locked == 0 && (
                        <>
                            <Button
                                color="light"
                                size="sm"
                                className="me-2"
                                title="Lock Payroll"
                                onClick={() => handleStatusUpdate(row.id, { is_locked: 1 })}
                            >
                                <i className="fas fa-lock text-danger"></i>
                            </Button>
                            <Button
                                style={greenBtnStyle}
                                size="sm"
                                title="Mark as Paid"
                                onClick={() => handleStatusUpdate(row.id, { status: "Paid", is_locked: 1 })}
                            >
                                <i className="fas fa-check-circle"></i> Pay
                            </Button>
                        </>
                    )}
                </div>
            )
        }
    ]

    const employeeColumns = [
        { dataField: "code", text: "EMP ID" },
        { dataField: "firstname", text: "NAME", formatter: (cell, row) => `${row.firstname} ${row.lastname}` },
        { dataField: "designation", text: "DESIGNATION" },
        { dataField: "basic_salary", text: "BASIC SALARY", formatter: (cell) => `₹${cell || 0}` },
        {
            dataField: "salary_group",
            text: "SALARY GROUP",
            formatter: (cell) => salaryGroups.find(g => g.id == cell)?.name || "Not Assigned"
        },
        {
            dataField: "earnings",
            text: "EARNINGS",
            formatter: (cell, row) => {
                const group = salaryGroups.find(g => g.id == row.salary_group);
                if (!group || !group.component_ids) return "---";
                const ids = group.component_ids.split(',');
                return (
                    <div style={{ fontSize: "11px" }}>
                        {ids.map(id => {
                            const comp = salaryComponents.find(c => c.id == id && c.type === 'Earning');
                            return comp ? (
                                <div key={id} className="text-success">
                                    {comp.name} ({comp.value_type === 'Fixed' ? `₹${comp.value}` : `${comp.value}%`})
                                </div>
                            ) : null;
                        })}
                    </div>
                );
            }
        },
        {
            dataField: "deductions",
            text: "DEDUCTIONS",
            formatter: (cell, row) => {
                const group = salaryGroups.find(g => g.id == row.salary_group);
                if (!group || !group.component_ids) return "---";
                const ids = group.component_ids.split(',');
                return (
                    <div style={{ fontSize: "11px" }}>
                        {ids.map(id => {
                            const comp = salaryComponents.find(c => c.id == id && c.type === 'Deduction');
                            return comp ? (
                                <div key={id} className="text-danger">
                                    {comp.name} ({comp.value_type === 'Fixed' ? `₹${comp.value}` : `${comp.value}%`})
                                </div>
                            ) : null;
                        })}
                    </div>
                );
            }
        },
        {
            dataField: "action",
            text: "ACTION",
            formatter: (cell, row) => (
                <Button color="light" size="sm" title="Edit Setup" onClick={() => {
                    setSetupData({
                        employee_id: row.code,
                        employee_name: `${row.firstname} ${row.lastname}`,
                        basic_salary: row.basic_salary || 0,
                        salary_group: row.salary_group || ""
                    })
                    setShowSetupModal(true)
                }}>
                    <i className="fas fa-pencil-alt text-primary"></i>
                </Button>
            )
        },
    ]

    const settingNavItems = [
        { id: "1", title: "Salary Component", component: <SalaryComponent /> },
        { id: "2", title: "Salary Group", component: <SalaryGroup /> },
        { id: "3", title: "SSF", component: <SSF /> },
        { id: "4", title: "Bonus", component: <Bonus /> },
        { id: "5", title: "Salary TDS", component: <SalaryTDS /> },
        { id: "6", title: "Advance Salary", component: <AdvanceSalary /> },
        { id: "7", title: "OverTime", component: <OverTime /> },
        { id: "8", title: "UnderTime", component: <UnderTime /> },
        { id: "9", title: "Payment Method", component: <PaymentMethod /> },
    ]

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>Dashboard | Payroll</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="HRMS" breadcrumbItem="Payroll" />

                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                <Nav tabs className="nav-tabs-custom border-bottom-0">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: activeTab === "1" })}
                                            onClick={() => toggleTab("1")}
                                            style={{ cursor: "pointer", border: "none" }}
                                        >
                                            <span className="d-block d-sm-none"><i className="fas fa-money-bill-wave"></i></span>
                                            <span className="d-none d-sm-block">Payroll</span>
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={classnames({ active: activeTab === '2' })} onClick={() => { toggleTab('2') }}>
                                            Salary Setup
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink className={classnames({ active: activeTab === '3' })} onClick={() => { toggleTab('3') }}>
                                            Settings
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                {activeTab === "1" && (
                                    <Button style={brownBtnStyle} size="sm" onClick={() => setShowGenerateModal(true)}>
                                        <i className="fas fa-plus me-1"></i> Generate Payroll
                                    </Button>
                                )}
                            </div>

                            <TabContent activeTab={activeTab} className="p-0 text-muted">
                                <TabPane tabId="1" className="pt-2">
                                    {activeTab === "1" && (
                                        <>
                                            <div className="text-uppercase mb-3" style={{ fontWeight: "bold", fontSize: "12px", color: "#555" }}>Payroll Filter</div>
                                            <Row className="mb-4 g-2">
                                                <Col md={3}>
                                                    <Select placeholder="Select Branch" options={branchOptions} />
                                                </Col>
                                                <Col md={3}>
                                                    <Select placeholder="Select Employee" options={employeeOptions} />
                                                </Col>
                                                <Col md={2}>
                                                    <Select placeholder="Month" options={months} />
                                                </Col>
                                                <Col md={2}>
                                                    <Select placeholder="Year" options={years} />
                                                </Col>
                                                <Col md={2} className="d-flex">
                                                    <Button style={greenBtnStyle} className="me-2 flex-grow-1">Filter</Button>
                                                    <Button style={brownBtnStyle} className="flex-grow-1">Reset</Button>
                                                </Col>
                                            </Row>

                                            <Datatables
                                                columns={payrollColumns}
                                                rows={payrollData.rows}
                                                keyField="id"
                                                rowsLength={payrollData.total_rows}
                                                isTableHead={false}
                                                isAdd={false}
                                                isSearch={true}
                                                loading={isLoading}
                                                ssr={setPagination}
                                            />
                                        </>
                                    )}
                                </TabPane>

                                <TabPane tabId="2">
                                    {activeTab === "2" && (
                                        <Datatables
                                            rows={employeeList}
                                            columns={employeeColumns}
                                            keyField="code"
                                            loading={isLoading}
                                            isRemote={false}
                                        />
                                    )}
                                </TabPane>

                                <TabPane tabId="3">
                                    {activeTab === "3" && (
                                        <PayrollSettingsView
                                            activeTab={activeSettingTab}
                                            toggleTab={toggleSettingTab}
                                            navItems={settingNavItems}
                                            pinkActiveStyle={pinkActiveStyle}
                                        />
                                    )}
                                </TabPane>
                            </TabContent>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <Modal isOpen={showGenerateModal} toggle={() => setShowGenerateModal(false)} centered>
                <ModalHeader toggle={() => setShowGenerateModal(false)}>Generate Payroll</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Branch</Label>
                            <Select
                                options={branchOptions}
                                onChange={(e) => setGenerateData(prev => ({ ...prev, branch_id: e.value }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Employee</Label>
                            <Select
                                options={employeeOptions}
                                onChange={(e) => setGenerateData(prev => ({ ...prev, employee_id: e.value }))}
                                defaultValue={employeeOptions[0]}
                            />
                        </FormGroup>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Month</Label>
                                    <Select
                                        options={months}
                                        defaultValue={months.find(m => m.value === generateData.month)}
                                        onChange={(e) => setGenerateData(prev => ({ ...prev, month: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Year</Label>
                                    <Select
                                        options={years}
                                        defaultValue={years.find(y => y.value === generateData.year)}
                                        onChange={(e) => setGenerateData(prev => ({ ...prev, year: e.value }))}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowGenerateModal(false)}>Cancel</Button>
                    <Button style={brownBtnStyle} onClick={handleGenerate}>Generate</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showSetupModal} toggle={() => setShowSetupModal(false)} centered>
                <ModalHeader toggle={() => setShowSetupModal(false)}>Employee Salary Setup</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label>Employee</Label>
                            <Input value={setupData.employee_name} disabled />
                        </FormGroup>
                        <FormGroup>
                            <Label>Basic Salary</Label>
                            <Input
                                type="number"
                                value={setupData.basic_salary}
                                onChange={(e) => setSetupData(prev => ({ ...prev, basic_salary: e.target.value }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Salary Group</Label>
                            <Select
                                options={salaryGroups.map(g => ({ value: g.id, label: g.name }))}
                                value={salaryGroups.filter(g => g.id == setupData.salary_group).map(g => ({ value: g.id, label: g.name }))[0]}
                                onChange={(e) => setSetupData(prev => ({ ...prev, salary_group: e.value }))}
                            />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowSetupModal(false)}>Cancel</Button>
                    <Button style={brownBtnStyle} onClick={handleSalarySetupUpdate}>Save Changes</Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    )
}

export default Payroll
